import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Minus, Trash2, ShoppingBag, ArrowLeft, Share2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api, fmtPrice } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

export default function Cart() {
  const { items, add, setQty, remove, subtotal, weight } = useCart();
  const [shipping, setShipping] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const [shared, setShared] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const sharedParam = searchParams.get("shared");

  useEffect(() => {
    if (weight > 0) api.get("/shipping/quote", { params: { weight_grams: weight, country: "PS" } }).then((r) => setShipping(r.data.cost));
    else setShipping(0);
  }, [weight]);

  useEffect(() => {
    api.get("/products", { params: { tag: "bestseller", limit: 4 } }).then((r) => {
      const skus = new Set(items.map((i) => i.sku));
      setSuggestions(r.data.filter((p) => !skus.has(p.sku)).slice(0, 4));
    });
  }, [items.length]);

  // Decode a shared cart link: /cart?shared=SKU:QTY,SKU:QTY
  useEffect(() => {
    if (!sharedParam) { setShared(null); return; }
    const pairs = sharedParam.split(",").map((s) => s.split(":")).filter((p) => p[0]);
    api.get("/products", { params: { limit: 200 } }).then((r) => {
      const map = {};
      r.data.forEach((p) => { map[p.sku] = p; });
      const list = pairs
        .map(([sku, q]) => map[sku] && { product: map[sku], qty: Math.min(99, Math.max(1, parseInt(q, 10) || 1)) })
        .filter(Boolean);
      setShared(list.length > 0 ? list : null);
    }).catch(() => {});
  }, [sharedParam]);

  const dismissShared = () => { setShared(null); setSearchParams(new URLSearchParams(), { replace: true }); };

  const importShared = () => {
    if (!shared) return;
    shared.forEach(({ product, qty }) => add(product, qty));
    dismissShared();
    toast.success("أُضيفت السلة المشتركة إلى سلتك");
  };

  const sharedTotal = shared ? shared.reduce((s, x) => s + x.product.price * x.qty, 0) : 0;

  const shareCart = async () => {
    const packed = items.map((i) => `${i.sku}:${i.qty}`).join(",");
    const url = `${window.location.origin}/cart?shared=${packed}`;
    const text = `سلة من سهول جنين (${items.reduce((s, i) => s + i.qty, 0)} منتجات)\n${url}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("تم نسخ رابط السلة — أرسله لصديقك");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); ta.remove();
        toast.success("تم نسخ رابط السلة — أرسله لصديقك");
      } catch { toast.error("تعذّر نسخ الرابط"); }
    }
  };

  const sharedBanner = shared && (
    <div data-testid="shared-cart-banner" className="mb-8 rounded-3xl border border-gold/40 bg-gold/[0.07] p-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-display text-lg font-bold text-olive-dark">سلة مشتركة وصلتك من صديق</div>
          <div className="mt-1 text-sm text-ink/60">{shared.length} منتجات · الإجمالي {fmtPrice(sharedTotal)}</div>
        </div>
        <div className="flex gap-2">
          <button data-testid="import-shared-cart" onClick={importShared}
            className="rounded-full bg-olive px-6 py-2.5 text-sm font-semibold text-ivory transition-colors hover:bg-olive-dark">
            أضفها إلى سلتي
          </button>
          <button data-testid="dismiss-shared-cart" onClick={dismissShared}
            className="rounded-full border border-olive/25 px-5 py-2.5 text-sm text-ink/60 transition-colors hover:bg-olive/5">
            تجاهل
          </button>
        </div>
      </div>
      <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
        {shared.map(({ product, qty }) => (
          <img key={product.sku} src={product.images?.main} alt={product.name_ar} title={`${product.name_ar} ×${qty}`}
            loading="lazy" decoding="async" className="h-14 w-14 shrink-0 rounded-xl object-cover ring-1 ring-olive/10" />
        ))}
      </div>
    </div>
  );

  if (items.length === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-5 px-6 pt-24 text-center">
        {sharedBanner}
        <ShoppingBag className="h-16 w-16 text-olive/30" />
        <h1 className="font-display text-2xl font-bold text-ink">سلتك فارغة</h1>
        <p className="text-ink/55">ابدأ باكتشاف منتجاتنا الفلسطينية الأصيلة.</p>
        <Link to="/store" className="rounded-full bg-olive px-7 py-3 font-semibold text-ivory">تصفّح المتجر</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24">
      <div className="mx-auto max-w-7xl px-6 py-10">
        <h1 className="mb-8 font-display text-3xl font-bold text-ink">سلتك</h1>
        {sharedBanner}
        <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
          <div className="space-y-4" data-testid="cart-items">
            {items.map((it) => (
              <div key={it.sku} data-testid={`cart-item-${it.sku}`} className="flex gap-4 rounded-2xl border border-olive/10 bg-white p-4">
                <Link to={`/product/${it.slug}`} className="h-24 w-24 shrink-0 overflow-hidden rounded-xl bg-sand">
                  <img src={it.image} alt={it.name_ar} className="h-full w-full object-cover" />
                </Link>
                <div className="flex flex-1 flex-col">
                  <Link to={`/product/${it.slug}`} className="font-display font-semibold text-ink hover:text-olive">{it.name_ar}</Link>
                  <span className="text-sm text-ink/50">{it.weight_grams} غم</span>
                  <div className="mt-auto flex items-center justify-between">
                    <div className="flex items-center rounded-full border border-olive/20">
                      <button data-testid={`cart-dec-${it.sku}`} onClick={() => setQty(it.sku, it.qty - 1)} className="flex h-9 w-9 items-center justify-center text-olive"><Minus className="h-3.5 w-3.5" /></button>
                      <span className="w-7 text-center text-sm font-semibold">{it.qty}</span>
                      <button data-testid={`cart-inc-${it.sku}`} onClick={() => setQty(it.sku, it.qty + 1)} className="flex h-9 w-9 items-center justify-center text-olive"><Plus className="h-3.5 w-3.5" /></button>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-display font-bold text-olive-dark">{fmtPrice(it.price * it.qty)}</span>
                      <button data-testid={`cart-remove-${it.sku}`} onClick={() => remove(it.sku)} className="text-ink/40 hover:text-palred"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <aside className="h-fit rounded-3xl border border-olive/10 bg-white p-6 lg:sticky lg:top-24">
            <h3 className="mb-4 font-display text-lg font-bold">ملخص السلة</h3>
            <Row label="الإجمالي الفرعي" value={fmtPrice(subtotal)} />
            <Row label="الوزن الإجمالي" value={`${(weight / 1000).toFixed(2)} كغ`} />
            <Row label="الشحن (تقديري)" value={fmtPrice(shipping)} />
            <div className="my-3 border-t border-olive/10" />
            <Row label="الإجمالي" value={fmtPrice(subtotal + shipping)} bold />
            <button data-testid="checkout-button" onClick={() => navigate("/checkout")}
              className="btn-shine mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-olive py-3.5 font-semibold text-ivory transition-colors hover:bg-olive-dark">
              إتمام الشراء <ArrowLeft className="h-4 w-4" />
            </button>
            <button data-testid="share-cart" onClick={shareCart}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-olive/25 py-3 text-sm font-semibold text-olive-dark transition-colors hover:bg-olive/5">
              <Share2 className="h-4 w-4" /> مشاركة السلة
            </button>
            <Link to="/store" className="mt-3 block text-center text-sm text-olive link-underline">متابعة التسوّق</Link>
          </aside>
        </div>

        {suggestions.length > 0 && (
          <div className="mt-16">
            <h2 className="mb-6 font-display text-xl font-bold text-ink">قد يناسب هذه السلة أيضاً...</h2>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:gap-6">
              {suggestions.map((p, i) => <ProductCard key={p.sku} product={p} index={i} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Row = ({ label, value, bold }) => (
  <div className={`flex items-center justify-between py-1.5 ${bold ? "font-display text-lg font-bold text-olive-dark" : "text-sm text-ink/65"}`}>
    <span>{label}</span><span>{value}</span>
  </div>
);
